import React, { useState } from "react";
import { Modal, Row, Col, ListGroup, Badge } from "react-bootstrap";
import API_ENDPOINTS from "../../config/api";

const PaymentDetailsModal = ({ show, onHide, request }) => {
  const [openLogId, setOpenLogId] = useState(null);

  if (!request) return null;

  // PHÂN TÍCH DỮ LIỆU:
  const attachments = request.attachments || [];

  const paymentTargetLabels = {
    0: "Supplier (Nhà cung cấp)",
    1: "Customer (Khách hàng)",
    2: "Internal (Nội bộ)"
  };

  // Ánh xạ Enum từ Backend (0: Draft, 1: Pending, 2: Approved, 3: Rejected)
  const statusLabels = {
    0: { text: "Draft", variant: "secondary" },
    1: { text: "Pending", variant: "warning" },
    2: { text: "Approved", variant: "success" },
    3: { text: "Rejected", variant: "danger" }
  };

  const handleDownload = async (url, fileName) => {
  try {
    // 1. Fetch dữ liệu file về dưới dạng Blob
    const response = await fetch(url);
    const blob = await response.blob();
    
    // 2. Tạo một đường dẫn tạm thời tới Blob đó
    const blobUrl = window.URL.createObjectURL(blob);
    
    // 3. Tạo thẻ <a> ảo và ép tải xuống
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    
    // 4. Dọn dẹp
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    // Nếu fetch thất bại (do CORS), fallback về link cũ
    window.open(url, '_blank');
  }
};

  const getDownloadUrl = (path) => {
  if (!path) return "#";
  const baseUrl = API_ENDPOINTS.url.replace(/\/$/, ""); // Xóa dấu / cuối nếu có
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // Nếu là local thì dùng đường dẫn gốc, nếu là server thì chèn /api
  return baseUrl.includes("localhost") 
    ? `${baseUrl}${cleanPath}` 
    : `${baseUrl}/api${cleanPath}`;
};

  const getStatusBadge = (statusKey) => {
    const statusInfo = statusLabels[statusKey] || { text: "Unknown", variant: "dark" };
    return <Badge bg={statusInfo.variant} className="px-3 py-2 text-uppercase">{statusInfo.text}</Badge>;
  };

  const ReadOnlyField = ({ label, vn, value }) => (
    <div className="mb-3">
      <label className="form-label font-weight-bold small text-muted">
        {label} <span className="text-muted font-weight-normal">({vn})</span>
      </label>
      <div className="form-control form-control-sm bg-light text-dark font-weight-bold" style={{ border: 'none', borderRadius: '4px' }}>
        {value || "-"}
      </div>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable centered>
      <Modal.Header className="bg-primary text-white py-3 px-4">
        <Modal.Title className="font-weight-bold">
          <i className="fas fa-file-invoice-dollar mr-2"></i> Payment Request Details
        </Modal.Title>
        <button type="button" className="close text-white" onClick={onHide}>&times;</button>
      </Modal.Header>

      <Modal.Body className="p-4">
        {/* --- TRẠNG THÁI PHIẾU --- */}
        <div className="d-flex align-items-center justify-content-between bg-light p-3 mb-4 rounded border">
          <div className="d-flex align-items-center">
            <span className="font-weight-bold mr-3">Status:</span>
            {getStatusBadge(request.status)}
          </div>
          <div>
            <span className="font-weight-bold mr-2">Current Step:</span>
            <Badge bg="primary">{request.currentStep || 1}</Badge>
          </div>
        </div>

        <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3">General Information (Thông tin chung)</h6>
        <Row>
          <Col md={6}><ReadOnlyField label="Title" vn="Tiêu đề" value={request.title} /></Col>
          <Col md={3}><ReadOnlyField label="Proposal Date" vn="Ngày đề xuất" value={request.requestDate} /></Col>
          <Col md={3}><ReadOnlyField label="Due Date" vn="Ngày đến hạn" value={request.dueDate} /></Col>
        </Row>
        <Row>
          <Col md={4}><ReadOnlyField label="Department" vn="Bộ phận" value={request.department?.name || request.departmentName} /></Col>
          <Col md={3}><ReadOnlyField label="Business Unit" vn="Đơn vị kinh doanh" value={request.businessUnit} /></Col>
          <Col md={5}><ReadOnlyField label="Payment Category" vn="Chi phí trực thuộc" value={request.costOwnership} /></Col>
        </Row>
        <Row>
          <Col md={4}><ReadOnlyField label="Cost Type" vn="Loại chi phí" value={request.costType} /></Col>
          <Col md={4}><ReadOnlyField label="Project" vn="Dự án" value={request.project?.name || "-"} /></Col>
          <Col md={4}><ReadOnlyField label="Purchase Order No" vn="Đơn hàng" value={request.orderCode} /></Col>
        </Row>
        <ReadOnlyField label="Payment Reason Details" vn="Chi tiết lý do bổ sung" value={request.paymentReasonDetails} />

        <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3 mt-3">Beneficiary & Bank Details (Thông tin thụ hưởng)</h6>
        <Row>
          <Col md={4}>
            <ReadOnlyField 
              label="Payment Target" 
              vn="Đối tượng thanh toán" 
              value={paymentTargetLabels[request.paymentTarget] || "Không xác định"} 
            />
          </Col>
          <Col md={4}><ReadOnlyField label="Company Name" vn="Tên công ty" value={request.supplier?.name} /></Col>
          <Col md={4}><ReadOnlyField label="Beneficiary Name" vn="Tên người thụ hưởng" value={request.beneficiaryName} /></Col>
        </Row>
        <Row>
          <Col md={4}><ReadOnlyField label="Account Number" vn="Số tài khoản" value={request.bankAccountNumber} /></Col>
          <Col md={4}><ReadOnlyField label="Bank Name" vn="Tên ngân hàng" value={request.bankName} /></Col>
          <Col md={4}><ReadOnlyField label="Transfer Content" vn="Nội dung chuyển khoản" value={request.transferContent} /></Col>
        </Row>

        <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3 mt-3">Financial & Invoice Details (Thông tin tài chính)</h6>
        <Row>
          <Col md={3}><ReadOnlyField label="Document Type" vn="Loại chứng từ" value={request.documentType} /></Col>
          <Col md={3}><ReadOnlyField label="Currency" vn="Tiền tệ" value={request.currency} /></Col>
          <Col md={3}>
            <label className="form-label font-weight-bold small text-muted">Total Amount <span className="text-muted font-weight-normal">(Tổng tiền)</span></label>
            <div className="text-success font-weight-bold mt-1" style={{ fontSize: '1.1rem' }}>
              {request.totalAmount ? new Intl.NumberFormat('vi-VN').format(request.totalAmount) : "0"}
            </div>
          </Col>
          <Col md={3}><ReadOnlyField label="Internal Doc Type" vn="Loại tài liệu nội bộ" value={request.internalDocumentType} /></Col>
        </Row>

   <div className="row mb-3">
  {/* --- KHU VỰC ATTACHED INVOICE (VIEW MULTI FROM API) --- */}
  <div className="col-md-6 mb-3">
    <label className="form-label font-weight-bold small text-muted">
      Invoice Files
    </label>
    <div>
      {request.paymentRequestFiles && request.paymentRequestFiles.filter(f => f.fileType === 0).length > 0 ? (
        request.paymentRequestFiles
          .filter(f => f.fileType === 0)
          .map((file, idx) => (
            <div key={file.id || idx} className="d-flex align-items-center justify-content-between bg-light p-1 rounded my-1 border">
              <button
                type="button"
                onClick={() => handleDownload(getDownloadUrl(file.fileUrl), file.fileName || `Invoice_${idx + 1}.pdf`)}
                className="btn btn-sm btn-link p-0 text-primary small text-truncate max-w-75"
                style={{ textDecoration: 'underline', border: 'none', background: 'none' }}
              >
                <i className="fas fa-file-download mr-1"></i>
                {file.fileName}
              </button>
            </div>
          ))
      ) : (
        <span className="text-muted small d-block p-1">No invoices attached</span>
      )}
    </div>
  </div>

  {/* --- KHU VỰC PAYMENT STATEMENT (VIEW MULTI FROM API) --- */}
  <div className="col-md-6 mb-3">
    <label className="form-label font-weight-bold small text-muted">
      Statement Files
    </label>
    <div>
      {request.paymentRequestFiles && request.paymentRequestFiles.filter(f => f.fileType === 1).length > 0 ? (
        request.paymentRequestFiles
          .filter(f => f.fileType === 1)
          .map((file, idx) => (
            <div key={file.id || idx} className="d-flex align-items-center justify-content-between bg-light p-1 rounded my-1 border">
              <button
                type="button"
                onClick={() => handleDownload(getDownloadUrl(file.fileUrl), file.fileName || `Statement_${idx + 1}.pdf`)}
                className="btn btn-sm btn-link p-0 text-primary small text-truncate max-w-75"
                style={{ textDecoration: 'underline', border: 'none', background: 'none' }}
              >
                <i className="fas fa-file-download mr-1"></i>
                {file.fileName}
              </button>
            </div>
          ))
      ) : (
        <span className="text-muted small d-block p-1">No statements attached</span>
      )}
    </div>
  </div>
</div>
        <h6 className="text-primary font-weight-bold text-uppercase border-bottom pb-2 mb-3 mt-4">Approval History (Lịch sử phê duyệt)</h6>
        <ListGroup variant="flush" className="mb-3 border rounded">
          {request.approvalLogs && request.approvalLogs.sort((a, b) => a.stepOrder - b.stepOrder).map((log) => {
              const files = attachments.filter(a => String(a.approvalLogId) === String(log.id));
              return (
                <ListGroup.Item key={log.id}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <span className="font-weight-bold text-dark">{log.approver?.employeeName || "System"}</span>
                      <div className="small text-muted">
                        Action: <Badge bg={log.status === 'Approved' ? 'success' : log.status === 'Rejected' ? 'danger' : 'warning'}>{log.status}</Badge> 
                        {log.comment && ` - Note: ${log.comment}`}
                      </div>
                    </div>
                    <div className="text-right">
                      <small className="d-block text-muted mb-1">{log.processedAt ? new Date(log.processedAt).toLocaleString() : "Pending"}</small>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setOpenLogId(openLogId === log.id ? null : log.id)}>
                        {openLogId === log.id ? "[-] Hide" : `[+] Files (${files.length})`}
                      </button>
                    </div>
                  </div>
                  {openLogId === log.id && (
                   <div className="mt-2 p-2 bg-light border-left border-primary" style={{ borderLeftWidth: '4px' }}>
                    {files.length > 0 ? (
                      files.map((file, fIdx) => (
                        <div key={fIdx} className="mb-1">
                          <button
                            type="button"
                            onClick={() => handleDownload(getDownloadUrl(file.fileUrl), file.fileName)}
                            className="btn btn-xs btn-link p-0 text-primary font-weight-bold"
                            style={{ border: 'none', background: 'none', textDecoration: 'underline' }}
                          >
                            <i className="fas fa-file-download mr-1"></i>
                            {file.fileName}
                          </button>
                        </div>
                      ))
                    ) : (
                      <small className="text-muted italic">No files attached</small>
                    )}
                  </div>
                  )}
                </ListGroup.Item>
              );
            })}
        </ListGroup>
      </Modal.Body>

      <Modal.Footer className="bg-light">
        <button className="btn btn-secondary btn-sm" onClick={onHide}>Close</button>
      </Modal.Footer>
    </Modal>
  );
};

export default PaymentDetailsModal;